// "Otro" en el rubro del onboarding de marca (ADR-2026-10-12-el-rubro-otro-se-cuenta-y-se-ajusta).
//
// Quien elige "Otro" escribe cuál es su rubro. Muchas veces lo que escribe SÍ es uno de la
// lista —"ropa de bebé", "velas aromáticas", "pastelería"— y eligió "Otro" porque no lo vio o
// no lo reconoció. El rubro de la empresa decide qué modos se le muestran, así que dejarlo en
// "Otro" le esconde los que le sirven: acá se busca a cuál de la lista corresponde.
//
// Lo decide Haiku en el backend al guardar, sin mostrarle nada a la persona (`rubroFinal`,
// ADR-2026-10-29-el-rubro-otro-se-ajusta-al-guardar):
// las palabras se equivocaban en lo obvio ("ropa de bebé" empataba Moda con Juguetería). Las
// palabras quedan de respaldo para cuando el backend no contesta. Lo que no encaja queda
// "Otro" y el texto viaja igual (a la memoria del agente y a Brevo).

import { atlasBackendFetch } from '../atlasBackend';

export const RUBRO_OTRO = 'Otro';

/** Raíces de palabras por rubro. Se comparan contra el texto sin acentos y en minúscula. */
const PALABRAS: ReadonlyArray<readonly [string, readonly string[]]> = [
  [
    'Moda e Indumentaria',
    [
      'ropa', 'indumentaria', 'moda', 'remera', 'camisa', 'pantalon', 'jean', 'vestido', 'pollera',
      'buzo', 'campera', 'abrigo', 'lenceria', 'malla', 'bikini', 'zapat', 'calzado', 'bota',
      'sandalia', 'cartera', 'bolso', 'mochila', 'accesorio', 'bijou', 'joya', 'joyeria', 'anillo',
      'collar', 'aro', 'reloj', 'lente', 'anteojo', 'gorra', 'sombrero', 'textil', 'tejido',
      'sweater', 'sueter', 'medias', 'pijama', 'uniforme', 'deportiva', 'boutique',
    ],
  ],
  [
    'Hogar y Decoración',
    [
      'hogar', 'deco', 'decoracion', 'mueble', 'muebleria', 'silla', 'mesa', 'sillon', 'sofa', 'cama', 'colchon',
      'almohad', 'sabana', 'blanqueria', 'cortina', 'alfombra', 'lampara', 'iluminacion', 'vela',
      'aromatizador', 'difusor', 'bazar', 'vajilla', 'cocina', 'jardin', 'planta', 'maceta',
      'interiorismo', 'carpinteria', 'ceramica', 'ferreteria', 'herramienta', 'pintureria',
    ],
  ],
  [
    'Belleza y Cuidado Personal',
    [
      'belleza', 'cosmetic', 'maquillaje', 'skincare', 'piel', 'crema', 'serum', 'perfum',
      'fragancia', 'pelo', 'cabello', 'peluqueria', 'barberia', 'shampoo', 'unas', 'manicur',
      'estetica', 'spa', 'depilacion', 'cuidado personal',
    ],
  ],
  [
    'Gastronomía',
    [
      'gastronom', 'comida', 'restaurant', 'resto', 'bar', 'cafe', 'cafeteria', 'panaderia',
      'pasteleria', 'reposteria', 'torta', 'alfajor', 'chocolate', 'helado', 'pizza', 'hamburguesa',
      'empanada', 'vino', 'cerveza', 'bebida', 'catering', 'delivery', 'vianda', 'almacen',
      'dietetica', 'alimento', 'snack', 'dulce', 'mermelada', 'fiambre', 'carniceria', 'verduleria',
    ],
  ],
  [
    'Salud y Bienestar',
    [
      'salud', 'bienestar', 'suplement', 'vitamina', 'farmacia', 'gimnasio', 'fitness', 'yoga',
      'pilates', 'nutricion', 'nutricionista', 'kinesio', 'fisioterap', 'psicolog', 'terapia',
      'medic', 'odontolog', 'dentista', 'ortopedia', 'masaje',
    ],
  ],
  [
    'Arte y Manualidades',
    [
      'arte', 'artesania', 'manualidad', 'pintura', 'cuadro', 'ilustracion', 'escultura', 'tejido a mano',
      'crochet', 'bordado', 'macrame', 'resina', 'lamina', 'grabado', 'fotografia artistica',
    ],
  ],
  [
    'Creación de Contenido',
    [
      'contenido', 'influencer', 'creador', 'creadora', 'youtube', 'tiktok', 'streamer', 'podcast',
      'blog', 'community manager', 'redes sociales', 'agencia', 'marketing',
    ],
  ],
  [
    'Tecnología',
    [
      'tecnolog', 'electronica', 'celular', 'telefono', 'computadora', 'notebook', 'gaming',
      'gamer', 'consola', 'auricular', 'parlante', 'software', 'app', 'informatica', 'gadget',
      'cargador', 'smartwatch',
    ],
  ],
  [
    'Librería',
    [
      'libreria', 'libro', 'papeleria', 'cuaderno', 'agenda', 'utiles', 'escolar', 'lapicera',
      'editorial', 'imprenta',
    ],
  ],
  [
    'Juguetería',
        // Sin "bebe" ni "infantil": dicen para quién es, no qué vende ("ropa de bebé" es Moda).
    ['jugueteria', 'juguete', 'juego de mesa', 'juegos de mesa', 'peluche', 'muñeco', 'muneco', 'didactico'],
  ],
  [
    'Automotriz',
    [
      'automotor', 'automotriz', 'auto', 'autos', 'moto', 'repuesto', 'neumatico', 'llanta',
      'taller mecanico', 'lubricentro', 'concesionaria', 'accesorios para auto',
    ],
  ],
];

const normalizar = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9ñ ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * El rubro de la lista al que corresponde lo que escribió, o null si ninguno encaja (o si
 * encajan dos con la misma fuerza: ante la duda, no se ajusta). Una raíz larga cuenta si
 * alguna palabra del texto EMPIEZA con ella ("zapat" → "zapatillas"); una de hasta 4 letras,
 * sólo si ES la palabra o su plural ("bar" no matchea "barbería", "auto" no matchea
 * "autoayuda"); las de varias palabras, si aparecen tal cual.
 */
export function rubroQueCorresponde(texto: string): string | null {
  const t = normalizar(texto);
  if (t.length < 3) return null;
  const palabras = t.split(' ');
  // Los puntos son las PALABRAS del texto que tocan el rubro, no las raíces que matchean: si
  // no, una palabra contaba dos veces ("muebleria" entra por "mueble" y por "muebleria";
  // "muñeco" y "muneco" son la misma raíz sin tilde) y desempataba a favor del rubro que
  // tiene raíces parecidas (review #174).
  const puntos = PALABRAS.map(([rubro, raices]) => {
    const tocadas = new Set<string>();
    for (const raiz of new Set(raices.map(normalizar))) {
      if (raiz.includes(' ')) {
        if (t.includes(raiz)) tocadas.add(raiz);
        continue;
      }
      for (const p of palabras) {
        const matchea =
          raiz.length <= 4 ? p === raiz || p === `${raiz}s` || p === `${raiz}es` : p.startsWith(raiz);
        if (matchea) tocadas.add(p);
      }
    }
    return [rubro, tocadas.size] as const;
  }).sort((a, b) => b[1] - a[1]);
  const [primero, segundo] = puntos;
  if (!primero || primero[1] === 0) return null;
  if (segundo && segundo[1] === primero[1]) return null;
  return primero[0];
}

/** Haiku tarda 1-2,5 s y el guardado lo espera: pasado esto, deciden las palabras. */
const ESPERA_MS = 6_000;
const clasificados = new Map<string, Promise<string | null>>();

/**
 * El rubro de la lista al que corresponde lo que escribió, según Haiku, o null si ninguno
 * encaja. Si el backend no contesta, decide `rubroQueCorresponde`. Un mismo texto se pregunta
 * una sola vez: "Armar mi marca" y el guardado esperan la misma respuesta.
 */
export function clasificarRubroOtro(texto: string): Promise<string | null> {
  const t = texto.trim();
  if (t.length < 3) return Promise.resolve(null);
  const enCurso = clasificados.get(t);
  if (enCurso) return enCurso;
  // El pedido se arma ADENTRO de la cadena: si armarlo tira (un navegador sin
  // `AbortSignal.timeout`, sin sesión), también decide el respaldo. Un rechazo dejaba el
  // guardado con el botón ocupado (review #219).
  const pedido = Promise.resolve()
    .then(() =>
      atlasBackendFetch('/v1/onboarding/rubro-otro', { texto: t }, { signal: AbortSignal.timeout(ESPERA_MS) }),
    )
    .then(async (res) => {
      const data = (await res.json().catch(() => null)) as { ok?: boolean; rubro?: unknown } | null;
      if (!res.ok || !data?.ok) throw new Error('el clasificador no contestó');
      return typeof data.rubro === 'string' ? data.rubro : null;
    })
    .catch(() => {
      // Sin respuesta no se guarda el fallo: el próximo pedido del mismo texto vuelve a probar.
      clasificados.delete(t);
      return rubroQueCorresponde(t);
    });
  clasificados.set(t, pedido);
  return pedido;
}

/**
 * El rubro que se guarda: el elegido, o —si eligió "Otro" y lo que escribió corresponde a uno
 * de la lista— ése.
 */
export async function rubroFinal(rubro: string, detalle: string): Promise<string> {
  if (rubro !== RUBRO_OTRO) return rubro;
  return (await clasificarRubroOtro(detalle)) ?? RUBRO_OTRO;
}
